import { createApiRef } from '@backstage/core-plugin-api';
import type {
  AccessRestriction,
  AdminAnalytics,
  AnalyticsRange,
  AvailabilityResponse,
  CreateSiteRequest,
  CreateSiteResponse,
  DeleteSiteResult,
  AdminCustomDomainRequest,
  CreateDomainResult,
  DnsCleanupNotice,
  CustomDomainRequest,
  DetachDomainResult,
  MeResponse,
  SiteBackend,
  SiteDirectoryEntry,
  SiteRecord,
  SiteSecret,
  SiteLifecycle,
  SiteTag,
  PublicDomain,
  R2Object,
  RedeployWorkerResult,
  RedeployWorkersReport,
  SiteAnalytics,
} from '@internal/plugin-spark-common';

/** Spark backend client. One ref; mock it whole in tests via `createSparkApi`. */
export interface SparkApi {
  // caller identity
  getMe(): Promise<MeResponse>;

  // sites
  createSite(req: CreateSiteRequest): Promise<CreateSiteResponse>;
  listSites(): Promise<SiteRecord[]>;
  listAllSites(): Promise<SiteDirectoryEntry[]>;
  getSite(name: string): Promise<SiteRecord>;
  checkAvailability(name: string): Promise<AvailabilityResponse>;
  deleteSite(name: string): Promise<DeleteSiteResult>;

  // files
  listObjects(siteName: string): Promise<R2Object[]>;
  uploadFiles(
    siteName: string,
    files: File[],
  ): Promise<{ uploaded: number } & ContentPublication>;
  deleteObjects(
    siteName: string,
    keys: string[],
  ): Promise<{ deleted: number } & ContentPublication>;

  // domains
  createDomain(
    siteName: string,
    fqdn: string,
    restriction?: AccessRestriction,
  ): Promise<CreateDomainResult>;
  detachDomain(siteName: string, domain: string): Promise<DetachDomainResult>;
  updateDomainAccess(
    siteName: string,
    domain: string,
    restriction: AccessRestriction,
  ): Promise<PublicDomain>;
  refreshDomain(siteName: string, domain: string): Promise<PublicDomain>;
  approveCustomDomain(siteName: string, domain: string): Promise<PublicDomain>;
  rejectCustomDomain(
    siteName: string,
    domain: string,
    reason?: string,
  ): Promise<CustomDomainRequest>;
  /** Removes a rejected request from the site so the owner can clear the notice. */
  dismissRejectedDomain(siteName: string, domain: string): Promise<void>;
  listCustomDomainRequests(): Promise<AdminCustomDomainRequest[]>;
  /** Detached custom hostnames whose public DNS records still need removing. */
  listDnsCleanup(): Promise<DnsCleanupNotice[]>;
  dismissDnsCleanup(domain: string): Promise<void>;

  // settings + sharing
  setCollaborators(
    siteName: string,
    collaborators: string[],
    owner?: string,
  ): Promise<{ owner: string; collaborators: string[] } & ContentPublication>;
  setBackends(
    siteName: string,
    backends: SiteBackend[],
  ): Promise<{ backends: SiteBackend[] } & ContentPublication>;
  /** Sets or rotates a write-only Worker secret. The value is never readable back. */
  setSecret(
    siteName: string,
    secretName: string,
    value: string,
  ): Promise<SiteSecret>;
  deleteSecret(siteName: string, secretName: string): Promise<void>;
  setDefaultPage(
    siteName: string,
    defaultPage: string | null,
  ): Promise<{ defaultPage: string | null } & ContentPublication>;
  setSiteProfile(
    siteName: string,
    profile: {
      description?: string;
      lifecycle: SiteLifecycle;
      tags: SiteTag[];
    },
  ): Promise<
    {
      description: string | null;
      lifecycle: SiteLifecycle;
      tags: SiteTag[];
    } & ContentPublication
  >;

  // workers + analytics
  redeployWorker(siteName: string): Promise<RedeployWorkerResult>;
  redeployAllWorkers(): Promise<RedeployWorkersReport>;
  getSiteAnalytics(
    siteName: string,
    range: AnalyticsRange,
  ): Promise<SiteAnalytics>;
  getAdminAnalytics(range: AnalyticsRange): Promise<AdminAnalytics>;
}

export interface ContentPublication {
  lastPublishedAt?: string;
  lastPublishedBy?: string;
}

export const sparkApiRef = createApiRef<SparkApi>({ id: 'plugin.spark' });
